import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, Check, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { SERVICES, IMAGES } from "./siteData";
import { Backdrop } from "./PageHero";
import { useSeo } from "./useSeo";
import { useEnquiry } from "./enquiryContext";

function Banner({ s }) {
  const openEnquiry = useEnquiry();
  return (
    <section className="bg-[#14263D] text-white relative isolate overflow-hidden" data-testid="service-banner">
      <Backdrop image={IMAGES[s.slug]} />
      <span className="site-outline-num absolute right-4 lg:right-16 bottom-[-40px] text-[220px] lg:text-[320px] select-none">{s.no}</span>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-24 lg:pb-32 site-reveal">
        <nav className="flex items-center gap-2 text-xs text-white/50" data-testid="service-breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link><ChevronRight className="h-3 w-3" />
          <Link to="/#expertise" className="hover:text-white">Verticals</Link><ChevronRight className="h-3 w-3" />
          <span className="text-[#C4A66A]">{s.title}</span>
        </nav>
        <div className="site-eyebrow mt-14">Vertical {s.no}</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold mt-5 max-w-3xl leading-[1.05]" data-testid="service-title">{s.title}</h1>
        <p className="mt-8 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">{s.hero}</p>
        <button onClick={() => openEnquiry({ service: s.title })} className="btn-site-gold mt-10 h-12 px-7 text-sm font-semibold inline-flex items-center gap-2" data-testid="service-hero-enquire-btn">
          Enquire about this vertical <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
}

function Scope({ s }) {
  const [active, setActive] = useState(0);
  const g = s.scope[active];
  return (
    <section className="py-24 lg:py-28 bg-white" data-testid="service-scope">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="site-eyebrow">Scope of services</div>
          <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#14263D] mt-4">What we do.</h2>
          <div className="mt-10 flex flex-col">
            {s.scope.map((x, i) => (
              <button key={x.title} onClick={() => setActive(i)}
                className={`text-left py-4 px-5 border-l-2 transition-colors duration-200 font-serif text-xl ${i === active ? "border-[#C4A66A] bg-[#F7F4EC] text-[#14263D] font-semibold" : "border-slate-200 text-slate-500 hover:text-[#14263D]"}`}
                data-testid={`scope-tab-${i}`}>{x.title}</button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-8 lg:pt-24" key={active}>
          <div className="site-reveal grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200" data-testid="scope-panel">
            {g.items.map((it) => (
              <div key={it} className="bg-white p-7 flex gap-4">
                <Check className="h-5 w-5 text-[#C4A66A] shrink-0 mt-0.5" />
                <span className="text-[15px] text-slate-700 leading-relaxed">{it}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Approach({ s }) {
  return (
    <section className="py-24 lg:py-28 bg-[#F7F4EC]" data-testid="service-approach">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="site-eyebrow">Methodology</div>
        <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#14263D] mt-4">How an engagement runs.</h2>
        <ol className="mt-14 grid md:grid-cols-4 gap-8">
          {s.approach.map((a, i) => (
            <li key={a.t} className="relative pt-8 border-t-2 border-[#14263D]" data-testid={`approach-step-${i}`}>
              <span className="absolute -top-[9px] left-0 h-4 w-4 bg-[#C4A66A]" />
              <div className="text-xs uppercase tracking-[0.25em] text-slate-500">Step {i + 1}</div>
              <h3 className="font-serif text-2xl font-semibold text-[#14263D] mt-2">{a.t}</h3>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{a.d}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Coverage({ s }) {
  return (
    <section className="py-24 bg-white" data-testid="service-coverage">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-16">
        <div>
          <div className="site-eyebrow">Who we work with</div>
          <ul className="mt-8">
            {s.clients.map((c) => <li key={c} className="py-4 border-b border-slate-200 font-serif text-xl text-[#14263D]">{c}</li>)}
          </ul>
        </div>
        <div>
          <div className="site-eyebrow">Frameworks covered</div>
          <div className="mt-8 flex flex-wrap gap-3">
            {s.frameworks.map((f) => <span key={f} className="px-4 py-2.5 border border-[#14263D]/15 text-sm text-[#14263D] bg-[#F8FAFC]">{f}</span>)}
          </div>
        </div>
      </div>
    </section>
  );
}

function Faq({ s }) {
  return (
    <section className="py-24 bg-white border-t border-slate-100" data-testid="service-faq">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="site-eyebrow">FAQ</div>
          <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#14263D] mt-4">Common questions.</h2>
        </div>
        <Accordion type="single" collapsible className="lg:col-span-8">
          {s.faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`f${i}`} className="border-slate-200">
              <AccordionTrigger className="font-serif text-xl text-left text-[#14263D] hover:no-underline py-6" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed text-[15px]">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function Cta({ s }) {
  const openEnquiry = useEnquiry();
  const others = SERVICES.filter((x) => x.slug !== s.slug);
  return (
    <section className="bg-[#14263D] text-white" data-testid="service-cta">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <h2 className="font-serif text-3xl lg:text-5xl font-semibold">Discuss your {s.title.toLowerCase()} requirement.</h2>
          <p className="text-white/60 mt-4">Your enquiry goes straight to the partner who leads this vertical.</p>
        </div>
        <div className="lg:col-span-5 lg:text-right">
          <button onClick={() => openEnquiry({ service: s.title })} className="btn-site-gold h-12 px-8 text-sm font-semibold inline-flex items-center gap-2" data-testid="service-cta-enquire-btn">Start an enquiry <ArrowUpRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid sm:grid-cols-2 lg:grid-cols-4">
          {others.map((o) => (
            <Link key={o.slug} to={`/expertise/${o.slug}`} className="group py-8 pr-6 flex items-center justify-between gap-4 border-b lg:border-b-0 border-white/10" data-testid={`other-service-${o.slug}`}>
              <span><span className="text-[#C4A66A] font-serif mr-3">{o.no}</span><span className="font-serif text-lg group-hover:text-[#C4A66A] transition-colors">{o.title}</span></span>
              <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-[#C4A66A]" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ServicePage() {
  const { slug } = useParams();
  const s = SERVICES.find((x) => x.slug === slug);
  const ld = useMemo(() => s && ({
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: s.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  }), [s]);
  useSeo(s?.seoTitle || "Verticals", s?.seoDesc || "", ld);
  if (!s) return <Navigate to="/" replace />;
  return (
    <div data-testid={`service-page-${s.slug}`}>
      <Banner s={s} />
      <Scope s={s} />
      <Approach s={s} />
      <Coverage s={s} />
      <Faq s={s} />
      <Cta s={s} />
    </div>
  );
}
